/* ══════════════════════════════════════════════
   EvalQUIZ.jsx — Creative Single-Module Design
   ══════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    Trophy,
    BookOpen,
    Play,
    Check,
    CheckCircle2,
    HelpCircle,
    AlertCircle,
    Loader,
    ArrowLeft,
    ArrowRight,
    Award,
    Sparkles,
    Timer,
    Clock,
    X,
    Lock
} from "lucide-react";

import { API_BASE_URL } from "../../../../api";
import { fetchLearnerModulesRequest } from "../../../redux/actions/learnerModulesActions";
import { fetchLearnerModulePlayerApi } from "../../../services/learnerModulesApi";
import {
    fetchLearnerQuizCurrentApi,
    fetchLearnerQuizHistoryApi,
    fetchLearnerQuizReviewApi,
    startLearnerQuizApi,
    submitLearnerQuizApi
} from "../../../services/moduleQuizApi";
import QuestionRenderer from "../../Formation/Apprenant/components/QuestionRenderer";
import QuizReviewPanel from "../../Formation/Apprenant/components/QuizReviewPanel";
import "../../Formation/Apprenant/components/QuizRunner.css";
import "./EvalQUIZ.css";

// Check if question type is supported
const SUPPORTED_TYPES = new Set(["QCU", "QCM", "OUI_NON", "COMPLETEMENT", "DISSERTATION", "MATCHING", "CLASSEMENT", "GAMING"]);
const isSupportedV1 = (question) => SUPPORTED_TYPES.has(question?.typeQuestion);

// Prepare default responses payload
const createInitialAnswers = (questions = []) =>
    questions.reduce((accumulator, question) => {
        if (question.typeQuestion === "QCM" || question.typeQuestion === "CLASSEMENT") {
            accumulator[question.id] = Array.isArray(question.submittedResponseJson)
                ? question.submittedResponseJson.map(String)
                : question.typeQuestion === "CLASSEMENT"
                    ? (question.reponsesPossibles || []).map((option) => String(option.id))
                    : [];
            return accumulator;
        }

        if (question.typeQuestion === "MATCHING") {
            accumulator[question.id] = question.submittedResponseJson && typeof question.submittedResponseJson === "object"
                ? question.submittedResponseJson
                : {};
            return accumulator;
        }

        if (question.typeQuestion === "GAMING") {
            accumulator[question.id] = question.submittedResponseJson && typeof question.submittedResponseJson === "object"
                ? question.submittedResponseJson
                : "";
            return accumulator;
        }

        if (question.submittedResponsePossibleId != null) {
            accumulator[question.id] = String(question.submittedResponsePossibleId);
            return accumulator;
        }

        if (typeof question.submittedResponseJson === "string") {
            accumulator[question.id] = question.submittedResponseJson;
            return accumulator;
        }

        accumulator[question.id] = question.typeQuestion === "QCM" ? [] : "";
        return accumulator;
    }, {});

const isAnswered = (question, value) => {
    if (!isSupportedV1(question)) return true;
    if (question.typeQuestion === "QCM") return Array.isArray(value) && value.length > 0;
    if (question.typeQuestion === "CLASSEMENT") {
        return Array.isArray(value) && value.length === (question.reponsesPossibles || []).length;
    }
    if (question.typeQuestion === "MATCHING") {
        const leftItems = question.configJson?.col_gauche || [];
        return leftItems.length > 0 && leftItems.every((item) => String(value?.[item] || "").trim().length > 0);
    }
    if (question.typeQuestion === "GAMING") {
        return Boolean(value?.selectedOptionId || value);
    }
    return String(value || "").trim().length > 0;
};

const buildAnswerPayload = (question, value) => {
    switch (question.typeQuestion) {
        case "QCU":
        case "OUI_NON":
            return {
                questionId: question.id,
                reponsePossibleId: value ? Number(value) : null,
                dureeReponseS: 0,
            };
        case "QCM":
            return {
                questionId: question.id,
                reponseJson: (Array.isArray(value) ? value : []).map((item) => Number(item)),
                dureeReponseS: 0,
            };
        case "COMPLETEMENT":
        case "DISSERTATION":
            return {
                questionId: question.id,
                reponseJson: String(value || ""),
                dureeReponseS: 0,
            };
        case "MATCHING":
            return {
                questionId: question.id,
                reponseJson: value && typeof value === "object" ? value : {},
                dureeReponseS: 0,
            };
        case "CLASSEMENT":
            return {
                questionId: question.id,
                reponseJson: (Array.isArray(value) ? value : []).map((item) => Number(item)),
                dureeReponseS: 0,
            };
        case "GAMING": {
            const selectedOptionId = value?.selectedOptionId ?? value;
            const selectedOption = (question.reponsesPossibles || []).find((option) => String(option.id) === String(selectedOptionId));
            return {
                questionId: question.id,
                reponseJson: {
                    selectedOptionId: selectedOptionId ? Number(selectedOptionId) : null,
                    correct: Boolean(selectedOption?.correct),
                },
                dureeReponseS: 0,
            };
        }
        default:
            return {
                questionId: question.id,
                dureeReponseS: 0,
            };
    }
};

export default function EvalQUIZ() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux selectors
    const { items = [], loading = false, error = null } = useSelector((state) => state.learnerModules || {});

    // Active skills modules
    const competencyModules = useMemo(() => {
        return (items || []).filter(m => m.type === "SKILLS");
    }, [items]);

    // Focus on the single assigned module
    const activeModule = useMemo(() => {
        return competencyModules[0] || null;
    }, [competencyModules]);

    // Local roadmap details
    const [roadmapData, setRoadmapData] = useState(null);
    const [roadmapLoading, setRoadmapLoading] = useState(false);

    // Active quiz session overlay state
    const [activeQuiz, setActiveQuiz] = useState(null);

    // Load initial modules list
    useEffect(() => {
        dispatch(fetchLearnerModulesRequest());
    }, [dispatch]);

    // Fetch quiz/roadmap steps dynamically for the active module
    const loadRoadmap = useCallback(async (moduleId) => {
        if (!moduleId) return;
        setRoadmapLoading(true);
        try {
            const response = await fetchLearnerModulePlayerApi(moduleId);
            if (response?.data) {
                setRoadmapData(response.data);
            }
        } catch (err) {
            console.error("Failed to load skills module roadmap", err);
        } finally {
            setRoadmapLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeModule?.id) {
            loadRoadmap(activeModule.id);
        }
    }, [activeModule?.id, loadRoadmap]);

    // Refresh everything upon player close
    const handleClosePlayer = useCallback(() => {
        setActiveQuiz(null);
        dispatch(fetchLearnerModulesRequest());
        if (activeModule?.id) {
            loadRoadmap(activeModule.id);
        }
    }, [activeModule?.id, dispatch, loadRoadmap]);

    // Flatten quizzes contents from sections
    const quizzesList = useMemo(() => {
        if (!roadmapData?.sections) return [];
        return roadmapData.sections.flatMap(section => 
            (section.contents || []).filter(c => c.contentType === "QUIZ" || c.contentType === "EVALUATION")
        );
    }, [roadmapData]);

    const completedQuizzesCount = useMemo(() => {
        return quizzesList.filter(q => q.progress?.status === "COMPLETED").length;
    }, [quizzesList]);

    const overallProgress = activeModule?.progress?.progressPercent || 0;

    return (
        <div className="eq-root">
            {/* ══ HEADER ══ */}
            <header className="eq-header">
                <div className="eq-header__top">
                    <div className="eq-header__brand">
                        <div className="eq-header__icon">
                            <Trophy size={18} color="#fff" />
                        </div>
                        <div>
                            <h1 className="eq-header__title">Portail d'Évaluation des Compétences</h1>
                            <p className="eq-header__sub">Validez vos acquis à travers des quiz thématiques rigoureux et ciblés</p>
                        </div>
                    </div>

                    {activeModule?.campagneNom && (
                        <div className="eq-header__campaign-badge">
                            <Award size={14} />
                            <span>{activeModule.campagneNom}</span>
                        </div>
                    )}
                </div>
            </header>

            {/* ══ MAIN BODY ══ */}
            <main className="eq-body">
                {loading || roadmapLoading ? (
                    <div className="eq-loading-state">
                        <Loader className="eq-spinner" size={32} />
                        <span>Préparation de votre parcours d'évaluation...</span>
                    </div>
                ) : error ? (
                    <div className="eq-empty-state">
                        <div className="eq-empty-state__icon" style={{ color: '#dc3545', borderColor: '#dc3545' }}>
                            <AlertCircle size={24} />
                        </div>
                        <h3 className="eq-empty-state__title">Erreur de chargement</h3>
                        <p className="eq-empty-state__text">{error}</p>
                    </div>
                ) : !activeModule ? (
                    <div className="eq-empty-state">
                        <div className="eq-empty-state__icon">
                            <Award size={24} />
                        </div>
                        <h3 className="eq-empty-state__title">Aucune évaluation assignée</h3>
                        <p className="eq-empty-state__text">
                            Vous n'avez pas de module d'évaluation de compétences affecté pour le moment.
                        </p>
                    </div>
                ) : activeQuiz ? (
                    <SkillsQuizPlayer
                        moduleId={activeModule.id}
                        content={activeQuiz}
                        onClose={handleClosePlayer}
                    />
                ) : (
                    /* CENTRAL HERO DASHBOARD & ROADMAP FLOW */
                    <>
                        {/* Hero module description and circular progression dial */}
                        <section className="eq-hero-card">
                            <div className="eq-hero-card__main">
                                <span className="eq-hero-card__kicker">Votre Évaluation de Compétence</span>
                                <h2 className="eq-hero-card__title">{activeModule.nom}</h2>
                                <p className="eq-hero-card__desc">
                                    {activeModule.description || "Complétez l'ensemble des quiz associés afin de positionner vos compétences dans le cadre de cette campagne."}
                                </p>
                                
                                <div className="eq-hero-card__meta">
                                    <div className="eq-meta-item">
                                        <BookOpen size={14} color="#2f4595" />
                                        <span>Parcours : <strong>{quizzesList.length} Quiz d'Évaluation</strong></span>
                                    </div>
                                    <div className="eq-meta-item">
                                        <CheckCircle2 size={14} color="#16a34a" />
                                        <span>Complétés : <strong>{completedQuizzesCount} / {quizzesList.length}</strong></span>
                                    </div>
                                </div>

                                {overallProgress === 100 && (
                                    <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <h4 style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Sparkles size={14} color="#2f4595" />
                                            <span>Toutes les évaluations sont complétées !</span>
                                        </h4>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                                            Vous avez complété l'ensemble de votre parcours d'évaluation. Vous pouvez dès à présent consulter votre radar ou comparer vos acquis par rapport à votre fiche de poste.
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                            <button 
                                                className="eq-btn eq-btn--primary" 
                                                style={{ fontSize: '11.5px', padding: '6px 12px' }}
                                                onClick={() => navigate("/skills/visualisation/ecarts")}
                                            >
                                                Écarts de poste
                                            </button>
                                            <button 
                                                className="eq-btn eq-btn--secondary" 
                                                style={{ fontSize: '11.5px', padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1' }}
                                                onClick={() => navigate("/skills/visualisation/radar")}
                                            >
                                                Mon Radar
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* SVG Radial Meter */}
                            <div className="eq-circular-progress-wrap">
                                <div className="eq-circular-progress">
                                    <svg viewBox="0 0 100 100">
                                        <circle 
                                            className="eq-circular-progress__bg" 
                                            cx="50" cy="50" r="40" 
                                        />
                                        <circle 
                                            className={`eq-circular-progress__fill${overallProgress === 100 ? ' completed' : ''}`}
                                            cx="50" cy="50" r="40" 
                                            strokeDasharray={`${2 * Math.PI * 40}`}
                                            strokeDashoffset={`${2 * Math.PI * 40 * (1 - overallProgress / 100)}`}
                                        />
                                    </svg>
                                    <div className="eq-circular-progress__text">
                                        {overallProgress === 100 ? (
                                            <Sparkles size={20} color="#16a34a" />
                                        ) : (
                                            <span>{overallProgress}%</span>
                                        )}
                                        <span className="eq-circular-progress__subtext">Complété</span>
                                    </div>
                                </div>
                            </div>
                        </section>



                        {/* Vertical Custom Timeline Roadmap */}
                        <section className="eq-roadmap">
                            {quizzesList.map((quiz, index) => {
                                const quizStatus = quiz.progress?.status || "NOT_STARTED";
                                const isQuizDone = quizStatus === "COMPLETED";
                                const isQuizProgress = quizStatus === "IN_PROGRESS";
                                const bestScore = quiz.progress?.progressPercent || 0;

                                // Steps tracking class
                                const stepClass = isQuizDone 
                                    ? "completed" 
                                    : isQuizProgress 
                                    ? "progress" 
                                    : "active";

                                return (
                                    <div key={quiz.id} className={`eq-roadmap-step ${stepClass}`}>
                                        {/* Step Circle Node */}
                                        <div className="eq-roadmap-marker">
                                            {isQuizDone ? (
                                                <Check size={11} strokeWidth={3} />
                                            ) : (
                                                <span className="eq-roadmap-num">{index + 1}</span>
                                            )}
                                        </div>

                                        {/* Step Content Card */}
                                        <article className={`eq-step-card ${isQuizDone ? 'completed' : ''}`}>
                                            <div className="eq-step-card__content">
                                                <h4 className="eq-step-card__title">
                                                    {quiz.nom}
                                                    {isQuizDone && (
                                                        <span className="eq-step-badge eq-step-badge--completed">Complété</span>
                                                    )}
                                                    {isQuizProgress && (
                                                        <span className="eq-step-badge eq-step-badge--progress">En cours</span>
                                                    )}
                                                </h4>
                                                {/*<p className="eq-step-card__desc">*/}
                                                {/*    {quiz.description || "Positionnez vos acquis sur cette compétence clé de votre poste."}*/}
                                                {/*</p>*/}

                                                <div className="eq-step-card__details">
                                                    {isQuizDone ? (
                                                        <span>
                                                            <Trophy size={12} style={{ color: '#2f4595' }} />
                                                            Score obtenu : <strong>{bestScore}%</strong>
                                                        </span>
                                                    ) : isQuizProgress ? (
                                                        <span>
                                                            <HelpCircle size={12} style={{ color: '#dc3545' }} />
                                                            En cours de positionnement
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            <HelpCircle size={12} />
                                                            Non commencé (À réaliser)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Dedicated skills launcher */}
                                            <div>
                                                <button
                                                    className={`eq-btn ${isQuizDone ? 'eq-btn--secondary' : 'eq-btn--primary'}`}
                                                    onClick={() => setActiveQuiz(quiz)}
                                                >
                                                    <Play size={10} fill={isQuizDone ? "#334155" : "#ffffff"} />
                                                    <span>
                                                        {isQuizDone 
                                                            ? "Revoir" 
                                                            : isQuizProgress 
                                                            ? "Reprendre" 
                                                            : "Lancer"}
                                                    </span>
                                                    <ArrowRight size={10} />
                                                </button>
                                            </div>
                                        </article>
                                    </div>
                                );
                            })}
                        </section>
                    </>
                )}
            </main>


        </div>
    );
}

// ══════════════════════════════════════════════
// DEDICATED IMMERSIVE QUIZ PLAYER COMPONENT
// ══════════════════════════════════════════════
function SkillsQuizPlayer({ moduleId, content, onClose }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Quiz Session States
    const [currentData, setCurrentData] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [activeAttemptData, setActiveAttemptData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [review, setReview] = useState(null);

    const handleOpenReview = async (tentativeId) => {
        if (!tentativeId) return;
        setActionLoading(true);
        setError(null);
        try {
            const response = await fetchLearnerQuizReviewApi(moduleId, content.id, tentativeId);
            setReview(response.data);
        } catch (requestError) {
            console.error("Failed to load review:", requestError);
            setError("Impossible de charger la relecture de la tentative.");
        } finally {
            setActionLoading(false);
        }
    };

    // Sequential Questions states
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    // Live Clock countdown
    const [timeLeftSeconds, setTimeLeftSeconds] = useState(null);
    const timerRef = useRef(null);

    const questions = useMemo(() => {
        return activeAttemptData?.questions || currentData?.questions || [];
    }, [activeAttemptData?.questions, currentData?.questions]);

    const supportedQuestions = useMemo(() => {
        return questions.filter(isSupportedV1);
    }, [questions]);

    // Load quiz data
    const refreshData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [currentResponse, historyResponse] = await Promise.all([
                fetchLearnerQuizCurrentApi(moduleId, content.id),
                fetchLearnerQuizHistoryApi(moduleId, content.id),
            ]);

            const currentPayload = currentResponse.data;
            setCurrentData(currentPayload);
            setHistoryData(historyResponse.data);

            if (currentPayload?.currentAttempt) {
                setActiveAttemptData(currentPayload);
                setAnswers(createInitialAnswers(currentPayload.questions));
            } else {
                setActiveAttemptData(null);
                // If quiz is completed, load the review of the latest attempt automatically!
                const latestTentativeId = currentPayload?.latestAttempt?.tentativeId || historyResponse.data?.attempts?.[0]?.tentativeId;
                if (latestTentativeId) {
                    try {
                        const reviewResponse = await fetchLearnerQuizReviewApi(moduleId, content.id, latestTentativeId);
                        setReview(reviewResponse.data);
                    } catch (revErr) {
                        console.error("Failed to load automatic review", revErr);
                    }
                }
            }
        } catch (requestError) {
            console.error(requestError);
            setError("Impossible de charger les données de cette évaluation.");
        } finally {
            setLoading(false);
        }
    }, [content.id, moduleId]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    // Timer countdown loop
    useEffect(() => {
        const activeAttempt = activeAttemptData?.attempt || currentData?.currentAttempt;
        
        if (activeAttempt && activeAttempt.dureeRestanteMs != null) {
            const initialSeconds = Math.max(0, Math.floor(activeAttempt.dureeRestanteMs / 1000));
            setTimeLeftSeconds(initialSeconds);

            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(() => {
                setTimeLeftSeconds((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        // Trigger auto-submit when timer expires
                        Swal.fire({
                            icon: 'warning',
                            title: 'Temps écoulé !',
                            text: 'Votre temps d\'évaluation est écoulé. Soumission automatique...',
                            timer: 2500,
                            showConfirmButton: false
                        }).then(() => {
                            autoSubmit();
                        });
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            setTimeLeftSeconds(null);
            if (timerRef.current) clearInterval(timerRef.current);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [activeAttemptData, currentData]);

    const formattedTimeLeft = useMemo(() => {
        if (timeLeftSeconds === null) return "--:--";
        const mins = Math.floor(timeLeftSeconds / 60);
        const secs = timeLeftSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, [timeLeftSeconds]);

    // Start a new evaluation attempt
    const handleStart = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const response = await startLearnerQuizApi(moduleId, content.id);
            setActiveAttemptData(response.data);
            setAnswers(createInitialAnswers(response.data.questions));
            setResult(null);
            setCurrentQuestionIndex(0);
            await refreshData();
        } catch (requestError) {
            console.error(requestError);
            setError("Échec lors du démarrage de la tentative d'évaluation.");
        } finally {
            setActionLoading(false);
        }
    };

    // Answer change handler
    const handleAnswerChange = (questionId, value) => {
        setAnswers((previous) => ({
            ...previous,
            [questionId]: value,
        }));
    };

    // Auto submit (triggered on timer expire)
    const autoSubmit = async () => {
        const tentativeId = activeAttemptData?.attempt?.tentativeId
            ?? activeAttemptData?.currentAttempt?.tentativeId
            ?? currentData?.currentAttempt?.tentativeId;

        if (!tentativeId) return;

        setActionLoading(true);
        try {
            const payload = {
                tentativeId,
                answers: supportedQuestions.map((question) => buildAnswerPayload(question, answers[question.id])),
            };
            const response = await submitLearnerQuizApi(moduleId, content.id, payload);
            setResult(response.data);
            setActiveAttemptData(null);
            await refreshData();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    // Standard User submit action
    const handleSubmit = async () => {
        const tentativeId = activeAttemptData?.attempt?.tentativeId
            ?? activeAttemptData?.currentAttempt?.tentativeId
            ?? currentData?.currentAttempt?.tentativeId;

        if (!tentativeId) {
            Swal.fire("Erreur", "Session d'évaluation introuvable.", "error");
            return;
        }

        // Check if any question remains unanswered
        const missingQuestions = supportedQuestions.filter((question) => !isAnswered(question, answers[question.id]));
        
        if (missingQuestions.length > 0) {
            const confirm = await Swal.fire({
                icon: "warning",
                title: "Questions non renseignées !",
                text: `Il reste ${missingQuestions.length} questions sans réponse. Voulez-vous tout de même soumettre ?`,
                showCancelButton: true,
                confirmButtonText: "Oui, soumettre",
                cancelButtonText: "Continuer l'évaluation",
                confirmButtonColor: "#dc3545"
            });
            if (!confirm.isConfirmed) return;
        } else {
            const confirm = await Swal.fire({
                icon: "question",
                title: "Fin de l'évaluation",
                text: "Êtes-vous sûr de vouloir soumettre vos réponses pour validation ?",
                showCancelButton: true,
                confirmButtonText: "Oui, valider",
                cancelButtonText: "Vérifier mes réponses",
                confirmButtonColor: "#fcd64c"
            });
            if (!confirm.isConfirmed) return;
        }

        setActionLoading(true);
        try {
            const payload = {
                tentativeId,
                answers: supportedQuestions.map((question) => buildAnswerPayload(question, answers[question.id])),
            };
            const response = await submitLearnerQuizApi(moduleId, content.id, payload);
            setResult(response.data);
            setActiveAttemptData(null);
            await refreshData();
        } catch (requestError) {
            console.error(requestError);
            Swal.fire("Erreur", "Une erreur est survenue lors de la soumission de l'évaluation.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleHeaderBack = () => {
        if (review) {
            if (result) {
                setReview(null);
            } else {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const attemptsRemaining = currentData?.attemptsRemaining ?? historyData?.attemptsRemaining ?? 0;
    const canStart = Boolean(currentData?.canStart) && !actionLoading;
    const activeAttempt = activeAttemptData?.attempt || currentData?.currentAttempt;
    const isQuizActive = Boolean(activeAttempt);

    // Calculate answers progress metrics
    const answeredCount = useMemo(() => {
        return supportedQuestions.filter((q) => isAnswered(q, answers[q.id])).length;
    }, [supportedQuestions, answers]);

    const activeQuestion = supportedQuestions[currentQuestionIndex];

    return (
        <div className="eq-player-overlay">
            {/* OVERLAY HEADER */}
            <header className="eq-player-header">
                <div className="eq-player-header__title-group">
                    <button className="eq-player-header__close" onClick={handleHeaderBack} aria-label="Retour">
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <span className="eq-player-header__eyebrow">
                            {review ? "Correction de l'évaluation" : "Évaluation en cours"}
                        </span>
                        <h2 className="eq-player-header__title">{content.nom}</h2>
                    </div>
                </div>

                {!review && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {isQuizActive && (
                            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>
                                Questions complétées : {answeredCount} / {supportedQuestions.length}
                            </div>
                        )}
                        <button className="eq-player-header__close" onClick={onClose} aria-label="Fermer le player">
                            <X size={18} />
                        </button>
                    </div>
                )}
            </header>

            {/* PLAYER MAIN WORKSPACE */}
            <div className="eq-player-workspace">
                {loading ? (
                    <div className="eq-loading-state" style={{ flex: 1 }}>
                        <Loader className="eq-spinner" size={32} />
                        <span>Chargement de la session d'évaluation...</span>
                    </div>
                ) : error ? (
                    <div className="eq-loading-state" style={{ flex: 1, color: '#dc3545' }}>
                        <AlertCircle size={32} />
                        <span>{error}</span>
                    </div>
                ) : review ? (
                    /* ════ QUIZ REVIEW MODE WORKSPACE ════ */
                    <div style={{ flex: 1, overflowY: 'auto', padding: '30px 40px', background: '#f8fafc' }}>
                        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                            <QuizReviewPanel 
                                review={review} 
                                onClose={handleHeaderBack} 
                            />
                        </div>
                    </div>
                ) : result ? (
                    /* ════ RESULTS DISPLAY DASHBOARD ════ */
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                        <div className="eq-results-container">
                            <div className="eq-results-banner success" style={{ background: '#2f4595' }} />
                            
                            <div className="eq-results-trophy success" style={{ background: '#eef2fb', color: '#2f4595' }}>
                                <CheckCircle2 size={36} />
                            </div>

                            <h2 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 6px 0', color: '#0f172a' }}>
                                Évaluation Complétée !
                            </h2>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 20px 0', maxWidth: '380px', lineHeight: 1.4 }}>
                                Votre évaluation a été enregistrée avec succès. Vos scores sont maintenant enregistrés dans votre référentiel de compétences.
                            </p>

                            <div className="eq-results-score-circle success" style={{ background: '#eef2fb', color: '#2f4595', border: '2.5px solid #2f4595' }}>
                                <span className="eq-results-score-val" style={{ color: '#2f4595' }}>{result.score}%</span>
                                <span className="eq-results-score-label" style={{ color: '#64748b' }}>Score de positionnement</span>
                            </div>

                            {/* Attempt stats history */}
                            <div className="eq-results-history" style={{ width: '100%', borderStyle: 'solid', marginTop: '16px' }}>
                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', display: 'block', textAlign: 'left', marginBottom: '6px' }}>
                                    Détails du positionnement
                                </span>
                                <div className="eq-results-history-item" style={{ marginBottom: '4px' }}>
                                    <span>Type de module :</span>
                                    <strong>Compétences (SKILLS)</strong>
                                </div>
                                <div className="eq-results-history-item">
                                    <span>Statut final :</span>
                                    <span className="eq-results-badge success" style={{ background: '#e6f4ea', color: '#137333', fontWeight: 'bold' }}>
                                        COMPLÉTÉ
                                    </span>
                                </div>
                            </div>

                            {/* Navigation CTAs */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: '20px' }}>
                                <button
                                    className="eq-btn eq-btn--primary"
                                    style={{ justifyContent: 'center', padding: '12px', fontSize: '13px', width: '100%' }}
                                    onClick={() => navigate("/skills/visualisation/ecarts")}
                                >
                                    Comparer mes écarts de poste
                                </button>
                                <button
                                    className="eq-btn eq-btn--secondary"
                                    style={{ justifyContent: 'center', padding: '12px', fontSize: '13px', width: '100%', border: '1px solid #cbd5e1' }}
                                    onClick={() => navigate("/skills/visualisation/radar")}
                                >
                                    Consulter mon radar de compétences
                                </button>
                                <button
                                    className="eq-btn eq-btn--secondary"
                                    style={{ justifyContent: 'center', padding: '12px', fontSize: '13px', width: '100%', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                                    onClick={() => handleOpenReview(result.attempt?.tentativeId || currentData?.latestAttempt?.tentativeId)}
                                >
                                    Revoir mes réponses et la correction
                                </button>
                                <button
                                    className="eq-btn"
                                    style={{ justifyContent: 'center', background: 'transparent', color: '#64748b', textDecoration: 'underline', fontSize: '12.5px', cursor: 'pointer', border: 'none', marginTop: '6px' }}
                                    onClick={onClose}
                                >
                                    Retour au parcours
                                </button>
                            </div>
                        </div>
                    </div>
                ) : isQuizActive ? (
                    /* ════ ACTIVE QUIZ SESSION WORKSPACE ════ */
                    <>
                        <main className="eq-player-main">
                            {supportedQuestions.length === 0 ? (
                                <div className="eq-question-box">
                                    <div className="eq-unsupported-warning">
                                        Aucune question valide ou supportée n'a été configurée pour cette évaluation.
                                    </div>
                                </div>
                            ) : (
                                <div className="eq-question-box">
                                    <div className="eq-question-header">
                                        <span className="eq-question-number">Question {currentQuestionIndex + 1} / {supportedQuestions.length}</span>
                                        <h3 className="eq-question-text">{activeQuestion?.enonce}</h3>
                                    </div>

                                    {/* Question input handler renderer */}
                                    <div style={{ minHeight: '140px' }}>
                                        <QuestionRenderer
                                            question={activeQuestion}
                                            value={answers[activeQuestion.id]}
                                            onChange={(val) => handleAnswerChange(activeQuestion.id, val)}
                                            disabled={actionLoading}
                                        />
                                    </div>

                                    {/* Question Nav controllers */}
                                    <div className="eq-question-nav">
                                        <button
                                            className="eq-btn eq-btn--secondary"
                                            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                                            disabled={currentQuestionIndex === 0}
                                        >
                                            Précédent
                                        </button>

                                        {currentQuestionIndex < supportedQuestions.length - 1 ? (
                                            <button
                                                className="eq-btn eq-btn--secondary"
                                                onClick={() => setCurrentQuestionIndex(prev => Math.min(supportedQuestions.length - 1, prev + 1))}
                                            >
                                                Suivant
                                                <ArrowRight size={12} />
                                            </button>
                                        ) : (
                                            <button
                                                className="eq-btn eq-btn--primary"
                                                onClick={handleSubmit}
                                                disabled={actionLoading}
                                            >
                                                Soumettre l'évaluation
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </main>

                        {/* RIGHT SIDEBAR COMPONENT */}
                        <aside className="eq-player-sidebar">
                            <div>
                                <h4 className="eq-sidebar-section-title">Temps Restant</h4>
                                <div className="eq-timer-clock">
                                    <Timer size={18} className="eq-timer-clock__icon" />
                                    <span className="eq-timer-clock__time">{formattedTimeLeft}</span>
                                </div>
                            </div>

                            <div>
                                <h4 className="eq-sidebar-section-title">Feuille de réponses</h4>
                                <div className="eq-question-index-grid">
                                    {supportedQuestions.map((q, idx) => {
                                        const isQActive = idx === currentQuestionIndex;
                                        const isQAnswered = isAnswered(q, answers[q.id]);
                                        
                                        let dotClass = "";
                                        if (isQActive) dotClass += " active";
                                        if (isQAnswered) dotClass += " answered";

                                        return (
                                            <button
                                                key={q.id}
                                                className={`eq-question-index-dot${dotClass}`}
                                                onClick={() => setCurrentQuestionIndex(idx)}
                                                title={`Aller à la question ${idx + 1}`}
                                                aria-label={`Aller à la question ${idx + 1}`}
                                            >
                                                {idx + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ marginTop: 'auto' }}>
                                <button
                                    className="eq-btn eq-btn--primary"
                                    style={{ width: '100%', justifyContent: 'center' }}
                                    onClick={handleSubmit}
                                    disabled={actionLoading}
                                >
                                    Soumettre maintenant
                                </button>
                            </div>
                        </aside>
                    </>
                ) : (
                    /* ════ QUIZ LOBBY SCREEN (BEFORE START) ════ */
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                        <div className="eq-results-container">
                            <div className="eq-results-banner success" style={{ background: '#2f4595' }} />
                            
                            <div className="eq-results-trophy" style={{ background: '#eef2fb', color: '#2f4595' }}>
                                <Clock size={36} />
                            </div>

                            <h2 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 6px 0', color: '#0f172a' }}>
                                Prêt à débuter l'évaluation ?
                            </h2>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 24px 0', maxWidth: '380px', lineHeight: 1.4 }}>
                                Cette évaluation vous permet de vous positionner par rapport aux compétences attendues pour votre poste. Prenez votre temps pour répondre précisément.
                            </p>

                            <div className="eq-results-history" style={{ width: '100%', borderStyle: 'solid' }}>
                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', display: 'block', textAlign: 'left', marginBottom: '6px' }}>
                                    Informations clés :
                                </span>
                                <div className="eq-results-history-item" style={{ marginBottom: '6px' }}>
                                    <span>Durée maximale :</span>
                                    <strong>{currentData?.quiz?.dureeMin || "--"} minutes</strong>
                                </div>
                                <div className="eq-results-history-item" style={{ marginBottom: '6px' }}>
                                    <span>Type de parcours :</span>
                                    <strong>Évaluation Individuelle</strong>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '12px' }}>
                                <button
                                    className="eq-btn eq-btn--primary"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={handleStart}
                                    disabled={!canStart}
                                >
                                    Commencer l'évaluation
                                </button>
                                <button
                                    className="eq-btn eq-btn--secondary"
                                    onClick={onClose}
                                >
                                    Retour
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
